import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import clamp from 'lodash/clamp';
import { annotationMetadataAtom } from '../atoms/annotation-atoms';
import { spanResizePreviewAtom } from '../atoms/frame-timeline-v4';
import { dispatchAtom } from '../atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../atoms/label-store-dispatch/events';
import { TIMELINE_EVENT } from '../constants/frame-timeline-v4';
import { useTimelineGestures } from '../providers/timeline-gestures';
import type { ActiveDragT, TimelineGestureDetailT } from '../types/frame-timeline-v4';
import { buildSpanResizeCommit, resolveSpanHandle, resolveSpanResizeBounds, toPreview } from '../utils/span-resize';
export function useSpanResizeController(totalFrames: number) {
  const {
    stage
  } = useTimelineGestures();
  const dispatch = useSetAtom(dispatchAtom);
  const metadata = useAtomValue(annotationMetadataAtom);
  const setPreview = useSetAtom(spanResizePreviewAtom);
  const dragRef = useRef<ActiveDragT | null>(null);
  const onDragStart = useCallback((event: CustomEvent<TimelineGestureDetailT>) => {
    const handle = resolveSpanHandle(event.detail.target);
    if (!handle) {
      return;
    }
    const {
      annotationId,
      isStart,
      edgeFrame
    } = handle;
    const spans = metadata.get(annotationId)?.spans ?? [];
    const bounds = resolveSpanResizeBounds(spans, isStart, edgeFrame, totalFrames);
    if (!bounds) return;
    dragRef.current = {
      annotationId,
      isStart,
      frame: edgeFrame,
      ...bounds
    };
    setPreview(toPreview(dragRef.current, edgeFrame));
  }, [metadata, totalFrames, setPreview]);
  const onDrag = useCallback((event: CustomEvent<TimelineGestureDetailT>) => {
    const drag = dragRef.current;
    if (!drag || event.detail.frame === null) {
      return;
    }
    const frame = clamp(event.detail.frame, ...drag.resizableRange);
    if (frame === drag.frame) return;
    drag.frame = frame;
    setPreview(toPreview(drag, frame));
  }, [setPreview]);
  const onDragEnd = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    if (!drag) {
      return;
    }
    dispatch({
      type: EVENTS.SET_CURRENT_FRAME,
      frameId: drag.frame
    });
    const commit = buildSpanResizeCommit(drag.annotationId, drag.span, drag.isStart, drag.frame);
    if (commit) {
      dispatch(commit);
    }
  }, [dispatch, setPreview]);
  useEffect(() => {
    stage.addEventListener(TIMELINE_EVENT.DRAG_START, onDragStart);
    stage.addEventListener(TIMELINE_EVENT.DRAG, onDrag);
    stage.addEventListener(TIMELINE_EVENT.DRAG_END, onDragEnd);
    return () => {
      stage.removeEventListener(TIMELINE_EVENT.DRAG_START, onDragStart);
      stage.removeEventListener(TIMELINE_EVENT.DRAG, onDrag);
      stage.removeEventListener(TIMELINE_EVENT.DRAG_END, onDragEnd);
    };
  }, [stage, onDragStart, onDrag, onDragEnd]);
}